<template>
  <div
    v-if="hasMenuItems"
    class="debate-options-menu"
    :class="{
      'debate-options-menu--always-visible': alwaysVisible || menuOpen,
      'debate-options-menu--header': placement === 'header'
    }"
  >
    <!--
      Trigger wrapper: q-menu must be a direct child of its anchor element.
      .stop blocks bubbling to router-link's onClick handler; .prevent blocks
      the browser default <a href> navigation when the menu lives inside a
      router-link (KdsDebateCard wraps the menu in an <a>).
    -->
    <div
      class="debate-options-menu__trigger"
      role="button"
      :aria-label="debateName ? t('debate.options.triggerAriaFor', { name: debateName }) : t('debate.options.triggerAria')"
      aria-haspopup="menu"
      :aria-expanded="menuOpen ? 'true' : 'false'"
      tabindex="0"
      @click.stop.prevent="menuOpen = !menuOpen"
      @keydown.enter.stop="menuOpen = !menuOpen"
      @keydown.space.prevent.stop="menuOpen = !menuOpen"
    >
      <KdsIcon name="more-horizontal" :size="16" class="debate-options-menu__trigger-icon" />

      <!--
        Disable Quasar's auto-attach click handler — we manage open state
        via our own @click to avoid double-toggle / immediate close.
      -->
      <q-menu
        v-model="menuOpen"
        no-parent-event
        auto-close
        anchor="bottom right"
        self="top right"
        class="debate-options-menu__dropdown"
        @show="emit('menu-open')"
        @hide="onMenuHide"
      >
        <div
          ref="menuListRef"
          role="menu"
          class="debate-options-menu__list"
          @keydown="onMenuKeydown"
        >
          <!-- Open debate (card only) -->
          <template v-if="showOpen">
            <div
              role="menuitem"
              class="debate-options-menu__item"
              tabindex="0"
              @click="onOpen"
              @keydown.enter.stop="onOpen"
              @keydown.space.prevent.stop="onOpen"
            >
              <KdsIcon name="external-link" :size="16" />
              <span>{{ t('debate.options.openDebate') }}</span>
            </div>
            <hr class="debate-options-menu__separator" />
          </template>

          <!-- Pause (running | seeding) -->
          <div
            v-if="state === 'running' || state === 'seeding'"
            role="menuitem"
            class="debate-options-menu__item"
            :class="{ 'debate-options-menu__item--loading': loading }"
            tabindex="0"
            :aria-disabled="loading ? 'true' : undefined"
            :style="loading ? { pointerEvents: 'none' } : undefined"
            @click="onPause"
            @keydown.enter.stop="onPause"
            @keydown.space.prevent.stop="onPause"
          >
            <q-spinner-dots v-if="loading" size="14px" class="debate-options-menu__spinner" />
            <KdsIcon v-else name="pause" :size="16" />
            <span>{{ t('debate.options.pauseDebate') }}</span>
          </div>

          <!-- Delete accessible from running | seeding (after Pause + separator) -->
          <template v-if="state === 'running' || state === 'seeding'">
            <hr class="debate-options-menu__separator" />
            <div
              role="menuitem"
              class="debate-options-menu__item debate-options-menu__item--danger"
              tabindex="0"
              @click="onDelete"
              @keydown.enter.stop="onDelete"
              @keydown.space.prevent.stop="onDelete"
            >
              <KdsIcon name="trash-2" :size="16" />
              <span>{{ t('debate.options.deleteDebate') }}</span>
            </div>
          </template>

          <!-- Resume + Delete (paused) -->
          <template v-if="state === 'paused'">
            <div
              role="menuitem"
              class="debate-options-menu__item"
              :class="{ 'debate-options-menu__item--loading': loading }"
              tabindex="0"
              :aria-disabled="loading ? 'true' : undefined"
              :style="loading ? { pointerEvents: 'none' } : undefined"
              @click="onResume"
              @keydown.enter.stop="onResume"
              @keydown.space.prevent.stop="onResume"
            >
              <q-spinner-dots v-if="loading" size="14px" class="debate-options-menu__spinner" />
              <KdsIcon v-else name="play" :size="16" />
              <span>{{ t('debate.options.resumeDebate') }}</span>
            </div>

            <hr class="debate-options-menu__separator" />

            <div
              role="menuitem"
              class="debate-options-menu__item debate-options-menu__item--danger"
              tabindex="0"
              @click="onDelete"
              @keydown.enter.stop="onDelete"
              @keydown.space.prevent.stop="onDelete"
            >
              <KdsIcon name="trash-2" :size="16" />
              <span>{{ t('debate.options.deleteDebate') }}</span>
            </div>
          </template>

          <!-- Delete only (failed) -->
          <div
            v-if="state === 'failed'"
            role="menuitem"
            class="debate-options-menu__item debate-options-menu__item--danger"
            tabindex="0"
            @click="onDelete"
            @keydown.enter.stop="onDelete"
            @keydown.space.prevent.stop="onDelete"
          >
            <KdsIcon name="trash-2" :size="16" />
            <span>{{ t('debate.options.deleteDebate') }}</span>
          </div>
        </div>
      </q-menu>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { KdsIcon } from '@/kds';
import type { DebateState } from '@/types/debate';

const props = defineProps<{
  debateId: string;
  debateName: string;
  state: DebateState;
  placement?: 'card' | 'header';
  loading?: boolean;
  alwaysVisible?: boolean;
  showOpen?: boolean;
}>();

const emit = defineEmits<{
  open: [debateId: string];
  pause: [debateId: string];
  resume: [debateId: string];
  delete: [debateId: string];
  'menu-open': [];
  'menu-close': [];
}>();

const { t } = useI18n();

const menuOpen = ref(false);
const menuListRef = ref<HTMLElement | null>(null);

/** Menu is only rendered for actionable states (including failed — delete only). */
const hasMenuItems = computed(
  () =>
    props.state === 'running' ||
    props.state === 'seeding' ||
    props.state === 'paused' ||
    props.state === 'failed'
);

const onMenuHide = (): void => {
  menuOpen.value = false;
  emit('menu-close');
};

const onOpen = (): void => {
  menuOpen.value = false;
  emit('open', props.debateId);
};

const onPause = (): void => {
  if (props.loading) return;
  menuOpen.value = false;
  emit('pause', props.debateId);
};

const onResume = (): void => {
  if (props.loading) return;
  menuOpen.value = false;
  emit('resume', props.debateId);
};

const onDelete = (): void => {
  menuOpen.value = false;
  emit('delete', props.debateId);
};

/**
 * ARIA menu keyboard navigation (ArrowDown / ArrowUp).
 * Moves focus to the next/previous focusable menuitem with wrap-around.
 * Per ARIA Authoring Practices Guide — menu pattern.
 */
const onMenuKeydown = (event: KeyboardEvent): void => {
  if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
  event.preventDefault();

  const list = menuListRef.value;
  if (!list) return;

  const items = Array.from(
    list.querySelectorAll<HTMLElement>('[role="menuitem"]:not([tabindex="-1"])')
  );
  if (items.length === 0) return;

  const currentIndex = items.findIndex((el) => el === document.activeElement);

  let nextIndex: number;
  if (event.key === 'ArrowDown') {
    nextIndex = currentIndex < items.length - 1 ? currentIndex + 1 : 0;
  } else {
    nextIndex = currentIndex > 0 ? currentIndex - 1 : items.length - 1;
  }

  items[nextIndex]?.focus();
};
</script>

<style scoped lang="scss">
.debate-options-menu {
  position: absolute;
  top: var(--space-3);
  right: var(--space-3);
  opacity: 0;
  transition: opacity var(--d-fast) var(--ease-out);
  z-index: 1;

  &--always-visible {
    opacity: 1;
  }

  &--header {
    position: relative;
    top: unset;
    right: unset;
    opacity: 1;
  }

  @media (hover: none) {
    opacity: 1;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
    opacity: 1;
  }

  &__trigger {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border-radius: var(--r-2);
    background: transparent;
    color: var(--ink);
    cursor: pointer;
    transition: background-color var(--d-fast) var(--ease-out);

    &:hover {
      background: var(--paper-3);
    }

    &:focus-visible {
      outline: none;
      box-shadow: var(--shadow-focus);
    }
  }

  &__trigger-icon {
    pointer-events: none;
  }

  &__dropdown {
    border-radius: var(--r-4);
    box-shadow: var(--shadow-2);
    overflow: hidden;
    min-width: 196px;
  }

  &__list {
    display: flex;
    flex-direction: column;
    padding: var(--space-2) 0;
  }

  &__item {
    display: flex;
    align-items: center;
    gap: var(--space-3);
    padding: var(--space-2) var(--space-4);
    cursor: pointer;
    font-size: var(--fs-body-sm);
    color: var(--ink);
    user-select: none;
    transition: background var(--d-fast) var(--ease-out);

    &:hover {
      background: var(--paper-3);
    }

    &:focus-visible {
      outline: none;
      box-shadow: var(--shadow-focus);
    }

    &--loading {
      opacity: 0.6;
      cursor: not-allowed;
    }

    &--danger {
      color: var(--danger);

      &:hover {
        background: color-mix(in srgb, var(--danger) 6%, transparent);
      }
    }
  }

  &__separator {
    border: none;
    border-top: var(--border);
    margin: var(--space-2) 0;
  }

  &__spinner {
    color: var(--stone);
  }
}
</style>
